import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { onAuthStateChange, auth } from './lib/supabase';
import type { User } from './types/database';

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import TripDetails from './pages/TripDetails';
import ChatPage from './pages/ChatPage';
import GlobalChatPage from './pages/GlobalChatPage';
import AuthPage from './pages/AuthPage';
import ItineraryPage from './pages/ItineraryPage';
import RoadTripPlanner from './pages/RoadTripPlanner';

// Auth Context
interface AuthContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    let isMounted = true;
    
    const { data: { subscription } } = onAuthStateChange((event, session) => {
      if (!isMounted) return;
      
      if (event === 'SIGNED_IN' && session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email!,
          created_at: session.user.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setLoading(false);
      }
    });

    // Check for existing session
    auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email!,
          created_at: session.user.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Routes>
        <Route path="/" element={<LandingPage user={user} />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/dashboard" element={user ? <Dashboard user={user} /> : <Navigate to="/auth" />} />
        <Route path="/chat" element={user ? <GlobalChatPage user={user} /> : <Navigate to="/auth" />} />
        <Route path="/trip/:tripId" element={user ? <TripDetails user={user} /> : <Navigate to="/auth" />} />
        <Route path="/trip/:tripId/chat" element={user ? <ChatPage user={user} /> : <Navigate to="/auth" />} />
        <Route path="/trip/:tripId/itinerary" element={user ? <ItineraryPage user={user} /> : <Navigate to="/auth" />} />
        <Route path="/trip/:tripId/road-trip" element={user ? <RoadTripPlanner user={user} /> : <Navigate to="/auth" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

export default App;
